import {
  ReactNode,
  useState,
  useMemo,
  useEffect,
  useCallback,
  SetStateAction,
} from 'react';
import {TranslatableResourceType} from 'types';
import {useShopLocalesAndMarkets} from 'utilities/use-shop-locales-and-markets';

import {TranslatableEntry} from '../types';

import {
  useQueryTranslatableResources,
  useTranslatableEntriesFromResources,
} from './hooks';
import TranslatableResourceContext from './TranslatableResourceContext';

export interface Props {
  children?: ReactNode;
  resourceId?: string;
  resourceType: TranslatableResourceType;
}

export default function TranslatableResourceProvider({
  children,
  resourceId,
  resourceType,
}: Props) {
  const {currentLocale, currentMarket} = useShopLocalesAndMarkets();
  const [initialLoading, setInitialLoading] = useState(true);
  const [translatableEntries, setTranslatableEntries] = useState<
    TranslatableEntry[]
  >([]);
  const {
    loading: translatableResourcesQueryLoading,
    resourceInformation,
    translatableResources,
    marketLocalizableResources,
    refetch,
    refetching,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
  } = useQueryTranslatableResources({
    resourceId,
    resourceType,
    locale: currentLocale,
    marketId: currentMarket?.id,
  });
  const [filteredTranslatableEntries, setFilteredTranslatableEntries] =
    useState<TranslatableEntry[] | null>(null);

  const localTranslatableEntries = useTranslatableEntriesFromResources({
    translatableResources,
    marketLocalizableResources,
    market: currentMarket,
  });

  const filterTranslatableEntries = useCallback(
    (
      nextState:
        | TranslatableEntry[]
        | null
        | ((
            previousTranslatableEntries: TranslatableEntry[],
          ) => SetStateAction<TranslatableEntry[] | null>),
    ) => {
      setFilteredTranslatableEntries(
        typeof nextState === 'function'
          ? nextState(translatableEntries)
          : nextState,
      );
    },
    [translatableEntries],
  );

  useEffect(() => {
    setTranslatableEntries(localTranslatableEntries);
  }, [localTranslatableEntries]);

  useEffect(() => {
    if (!translatableResourcesQueryLoading) {
      setInitialLoading(false);
    }
  }, [translatableResourcesQueryLoading]);

  /**
   * Reset states when the resource type or id changes
   */
  useEffect(() => {
    setFilteredTranslatableEntries(null);
    setTranslatableEntries([]);
  }, [resourceType, resourceId, currentLocale.locale, currentMarket?.handle]);

  /**
   * Reset states when the resource type changes
   */
  useEffect(() => {
    setInitialLoading(true);
  }, [resourceType]);

  const translatableResource = useMemo(() => {
    return translatableResources.find(({resourceId: id}) => id === resourceId);
  }, [translatableResources, resourceId]);

  const contextValue = useMemo(
    () => ({
      resourceId,
      resourceType,
      resourceDisplayTitle: translatableResource?.displayTitle ?? '',
      resourceDisplayImage: translatableResource?.displayImage ?? null,
      resourceAdminIdType: translatableResource?.resourceAdminIdType ?? null,
      initialLoading,
      translatableResourcesQueryLoading,
      resourceInformation,
      translatableEntries,
      setTranslatableEntries,
      filteredTranslatableEntries:
        filteredTranslatableEntries ?? translatableEntries,
      filterTranslatableEntries,
      refetch,
      refetching,
      hasNextPage,
      hasPreviousPage,
      fetchNextPage,
      fetchPreviousPage,
    }),
    [
      resourceId,
      resourceType,
      translatableResource?.displayTitle,
      translatableResource?.displayImage,
      translatableResource?.resourceAdminIdType,
      initialLoading,
      translatableResourcesQueryLoading,
      resourceInformation,
      translatableEntries,
      filteredTranslatableEntries,
      filterTranslatableEntries,
      refetch,
      refetching,
      hasNextPage,
      hasPreviousPage,
      fetchNextPage,
      fetchPreviousPage,
    ],
  );

  return (
    <TranslatableResourceContext.Provider value={contextValue}>
      {children}
    </TranslatableResourceContext.Provider>
  );
}
